import React, { useState, useEffect, Fragment } from 'react';
import { useHistory, Link, useParams } from 'react-router-dom';
import Select from 'react-select';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip, Button, CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CopyrightIcon from '@mui/icons-material/Copyright';
// Assume fetchData and postData are defined elsewhere
import { fetchData, updateData, postData } from '../../../apiService';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import AddIcon from "@mui/icons-material/Add";
import AsyncCreatableSelect from 'react-select/async-creatable';

const InvoiceDetails = () => {
    const { id } = useParams(); // Use useParams to get the id from the URL  
    const history = useHistory();
    const { isAuthenticated, companyId, branchId, userId, logout, token } = useAuth();

    useEffect(() => {
        if (isAuthenticated && tokenIsExpired(token)) {
            logout();
            return;
        }
        fetchStatus();
    }, [isAuthenticated]);

    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [formData, setFormData] = useState({
        CompanyId: companyId,
        CreatedBy: userId,
        CreatedDate: new Date().toISOString(),
    });

    const [containerItems, setContainerItems] = useState([]);
    const [expenseItems, setExpenseItems] = useState([]);

    const formatDateForInput = (dateString) => {
        const d = new Date(dateString);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };

    const [errorMessageModel, setErrorMessageModel] = useState('');

    // Expand/Collapse state    
    const [isExpanded, setIsExpanded] = useState(true);
    const [isExpandedExpense, setIsExpandedExpense] = useState(true);

    useEffect(() => {
        fetchData('InvoiceApi', `/${id}`, token)
            .then((response) => {
                if (response.responseCode === '1111') {
                    setFormData(response.response_data.Invoice);
                    setContainerItems(response.response_data.InvoiceContainerDetails);
                    setExpenseItems(response.response_data.InvoiceExpenseDetails);
                }
            })
            .catch((error) => {
                console.error('Error fetching Invoice Details:', error);
            });
    }, [id]);

    // Status Update
    const [StatusOptions, setStatusOptions] = useState([]);
    const [selectedStatus, setSelectedStatus] = useState("");
    const fetchStatus = async () => {
        try {
            const res = await fetchData('ConsignmentStatusApi', `/${companyId}`, token);
            if (res.responseCode === '1111') {
                const options = res.response_data.map(c => ({ value: c.Id, label: c.Name }));
                setStatusOptions(options);
            }
        } catch (e) { console.error('Error fetching Status:', e); }
    };

    const handleStatusChange = (opt) => {
        setSelectedStatus(opt);
        setFormData(prev => ({
            ...prev,
            ConsignmentStatusId: opt?.value || 0,
            ConsignmentStatusName: opt?.label || '',
        }));
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setIsLoading(true);

        // ✅ Update state first
        setFormData(prev => ({
            ...prev,
            ConsignmentStatusId: selectedStatus?.value || formData.ConsignmentStatusId || 0,
            ConsignmentStatusName: selectedStatus?.label || formData.ConsignmentStatusName || '',
        }));

        // ✅ Then prepare payload object
        const UpdateConsignmentStatusOnInvoice = {
            Id: id,
            ModifiedDate: new Date().toISOString(),
            ModifiedBy: userId,
            CompanyId: companyId,
            ConsignmentStatusId: selectedStatus?.value || formData.ConsignmentStatusId || 0,
            ConsignmentStatusName: selectedStatus?.label || formData.ConsignmentStatusName || '',
        };

        updateData('ConsignmentStatusApi', `${id}`, UpdateConsignmentStatusOnInvoice, token)
            .then((response) => {
                console.log('Record updated successfully:', response.data);
                setIsLoading(false);
                window.location.reload();
            })
            .catch((error) => {
                console.error('Error updating record:', error);
                setIsLoading(false);
            });
    };

    return (
        <Fragment>
            <div className="col-span-2 md:col-span-6 flex flex-col md:flex-row justify-between items-center mb-4 gap-4" style={{ marginTop: '-40px' }}>
                <h6 className="uppercase tracking-wide font-serif text-black mt-2">
                    Consignment  Details
                </h6>
                <div className="flex items-center gap-4 mt-2 md:mt-0">
                    <div className="flex items-center w-full mr-2">
                        {/* Label */}
                        <label
                            className="text-black text-xs uppercase mr-2"
                            style={{ fontFamily: "monospace", fontSize: "14px" }}
                        >
                            Status
                        </label>

                        {/* Dropdown */}
                        <div className="flex-1">
                            <Select
                                value={
                                    selectedStatus ||
                                    (formData.ConsignmentStatusName
                                        ? { value: formData.ConsignmentStatusId, label: formData.ConsignmentStatusName }
                                        : null)
                                }
                                onChange={handleStatusChange}
                                options={StatusOptions}
                                placeholder="Status"
                                isSearchable
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: "30px",
                                        height: "30px",
                                        fontSize: "12px",
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        height: "30px",
                                        padding: "0 6px",
                                    }),
                                    indicatorsContainer: (base) => ({
                                        ...base,
                                        height: "30px",
                                    }),
                                    menu: (provided) => ({
                                        ...provided,
                                        zIndex: 1301,
                                        fontSize: "12px",
                                    }),
                                }}
                            />
                        </div>
                    </div>

                    {/* Update Status Button */}
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        style={{
                            width: "200px",
                            height: "27px",
                            borderRadius: "10px",
                            marginLeft: "10px",
                            fontSize: "11.5px",
                            backgroundColor: "#0c3102ff",
                        }}
                    >
                        Update Status
                    </Button>


                    {/* Back to List Button */}
                    <Button
                        variant="contained"
                        color="error"
                        component={Link}
                        to={{
                            pathname: "/InvoiceIndex",
                            state: { from: "/InvoiceCreate" },
                        }}
                        onClick={() => localStorage.setItem("shouldReload", "true")}
                        style={{ width: "175px", height: "25px", borderRadius: "10px", marginRight: "2px", fontSize: '11.5px' }}
                    >
                        Back To List
                    </Button>
                </div>

            </div >

            <div className="w-full flex flex-wrap"
                style={{ backgroundColor: 'whitesmoke', padding: '7px', borderRadius: '10px', marginTop: '-15px', boxShadow: "0 6px 10px rgba(0, 0, 0, 0.25), 0 12px 25px rgba(0, 0, 0, 0.2)" }}
            >
                <div className="w-full pr-4" style={{ marginTop: '-0.21%' }}>
                    <div className="w-full mb-2 flex">
                        <div className="w-4/5 mr-2">
                            <label className="block uppercase text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '14px' }}>
                                Customer
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={formData.CustomerName || ''}
                                type="text"
                                name='CustomerName'
                                style={{ height: '30px', fontSize: '12.5px' }}
                                readOnly
                            />
                        </div>
                        <div className="w-4/5 mr-2">
                            <label className="block uppercase text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Consignee
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={formData.ConsigneeName || ''}
                                type="text"
                                name='ConsigneeName'
                                style={{ height: '30px', fontSize: '14px' }}
                                readOnly
                            />
                        </div>
                        <div className="w-2/5 mr-2">
                            <label className="block uppercase text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Consignment Type
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={formData.ConsignmentName || ''}
                                type="text"
                                name='ConsignmentName'
                                style={{ height: '30px', fontSize: '14px' }}
                                readOnly
                            />
                        </div>

                        <div className="w-1/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Invoice Date
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={formatDateForInput(formData.InvoiceDate)}
                                type="date"
                                name='InvoiceDate'
                                style={{ height: '30px', fontSize: '14px' }}
                                disabled
                            />
                        </div>

                        <div className="w-2/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Invoice Number
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                // value={formData.InvoiceNumber}
                                value={`${formData.BillPrefix}/${formData.InvoiceNumber}/${formData.InvoiceDate ? new Date(formData.InvoiceDate).getFullYear() : ''}`}
                                type="text"
                                placeholder="Invoice Number"
                                name='InvoiceNumber'
                                style={{ height: '30px', fontSize: '14px' }}
                                readOnly
                            />
                        </div>

                        <div className="w-3/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                BL Number
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={formData.BLNumber}
                                type="text"
                                placeholder="BL Number"
                                name='BLNumber'
                                style={{ height: '30px', fontSize: '14px' }}
                                readOnly
                            />
                        </div>

                    </div>

                    <div className="w-full mb-2 flex">
                        <div className="w-3/5 mr-2">
                            <label className="block uppercase text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Remarks 1
                            </label>
                            <textarea
                                className="w-full border rounded p-2 text-gray-700 focus:outline-none"
                                name="Remarks1"
                                rows="1"
                                value={formData.Remarks1}
                                maxLength={200}
                                readOnly
                                style={{ height: '30px', fontSize: '13px' }}
                            />
                        </div>
                        <div className="w-3/5 mr-2">
                            <label className="block uppercase text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Remarks 2
                            </label>
                            <textarea
                                className="w-full border rounded p-2 text-gray-700 focus:outline-none"
                                name="Remarks2"
                                rows="1"
                                value={formData.Remarks2}
                                maxLength={200}
                                readOnly
                                style={{ height: '30px', fontSize: '13px' }}
                            />
                        </div>
                        <div className="w-1/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }} >
                                Advance Amount
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={
                                    formData.AdvanceAmount != null && formData.AdvanceAmount !== ""
                                        ? parseFloat(formData.AdvanceAmount).toFixed(3)
                                        : ""
                                }
                                type="text"
                                placeholder="Advance Amount"
                                name='AdvanceAmount'
                                style={{ height: '30px', textAlign: 'right', fontSize: '14px', fontWeight: 'bold' }}
                                readOnly
                            />
                        </div>

                        <div className="w-1/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Taxable Amount
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={
                                    formData.TaxableAmount != null && formData.TaxableAmount !== ""
                                        ? parseFloat(formData.TaxableAmount).toFixed(3)
                                        : ""
                                }
                                type="text"
                                placeholder="TaxableAmount"
                                name='TaxableAmount'
                                style={{ height: '30px', textAlign: 'right', fontSize: '14px', fontWeight: 'bold' }}
                                readOnly
                            />
                        </div>

                        <div className="w-1/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }} >
                                Vat Amount
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={
                                    formData.VatAmount != null && formData.VatAmount !== ""
                                        ? parseFloat(formData.VatAmount).toFixed(3)
                                        : ""
                                }
                                type="text"
                                placeholder="Vat Amount"
                                name='VatAmount'
                                style={{ height: '30px', textAlign: 'right', fontSize: '14px', fontWeight: 'bold' }}
                                readOnly
                            />
                        </div>

                        <div className="w-1/5 mr-2">
                            <label className="block uppercase tracking-wide text-black text-xs mb-1" style={{ fontFamily: 'monospace', fontSize: '12.5px' }}>
                                Total Amount
                                <span className="text-red-500">*</span>
                            </label>
                            <input
                                className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:text-gray-600 focus:shadow-outline"
                                value={
                                    formData.TotalAmount != null && formData.TotalAmount !== ""
                                        ? parseFloat(formData.TotalAmount).toFixed(3)
                                        : ""
                                }
                                type="text"
                                placeholder="TotalAmount"
                                name='TotalAmount'
                                style={{ height: '30px', textAlign: 'right', fontSize: '14px', fontWeight: 'bold' }}
                                readOnly
                            />
                        </div>

                    </div>
                </div>

            </div>

            <div
                className="w-full flex flex-col"
                style={{
                    backgroundColor: "whitesmoke",
                    padding: "7px",
                    borderRadius: "10px",
                    marginTop: "10px",
                    boxShadow: "0 6px 10px rgba(0, 0, 0, 0.25), 0 12px 25px rgba(0, 0, 0, 0.2)"
                }}
            >
                {/* Header with expand/collapse button */}
                <div className="flex justify-between items-center">
                    <h6
                        className="uppercase tracking-wide font-serif mt-2"
                        style={{ color: "#1b023dff", fontSize: '14px' }}
                    >
                        Container List
                    </h6>
                    {errorMessageModel && (
                        <span style={{ color: 'red', fontSize: '13px', marginLeft: '10px' }}>
                            {errorMessageModel}
                        </span>
                    )}

                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="text-sm text-blue-600 flex items-center gap-1"
                        style={{ color: "#1b023dff" }}
                    >
                        {isExpanded ? (
                            <>
                                <ExpandLessIcon size={16} />
                            </>
                        ) : (
                            <>
                                <ExpandMoreIcon size={16} />
                            </>
                        )}
                    </button>
                </div>

                {/* Expandable Section */}
                {isExpanded && (
                    <div className="w-full mt-1">
                        {/* Table Section */}
                        <div className="w-full pr-4" style={{ marginTop: "10px" }}>
                            <div
                                className="mb-2 flex"
                                style={{
                                    maxHeight: "450px",
                                    overflowY: "auto",
                                    marginTop: "-15px",
                                }}
                            >
                                <TableContainer component={Paper}>
                                    <Table stickyHeader>
                                        <TableHead stickyHeader>
                                            <TableRow>
                                                <TableCell
                                                    style={{
                                                        fontSize: "11px",
                                                        backgroundColor: "#435560",
                                                        padding: "1px",
                                                        color: "whitesmoke",
                                                        textTransform: "uppercase",
                                                        fontWeight: "bolder",
                                                    }}
                                                >
                                                    Sl.No
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        fontSize: "11px",
                                                        backgroundColor: "#435560",
                                                        padding: "1px",
                                                        color: "whitesmoke",
                                                        textTransform: "uppercase",
                                                        fontWeight: "bolder",
                                                    }}>
                                                    Container Number
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        fontSize: "11px",
                                                        backgroundColor: "#435560",
                                                        padding: "1px",
                                                        color: "whitesmoke",
                                                        textTransform: "uppercase",
                                                        fontWeight: "bolder",
                                                    }}>
                                                    Source
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        fontSize: "11px",
                                                        backgroundColor: "#435560",
                                                        padding: "1px",
                                                        color: "whitesmoke",
                                                        textTransform: "uppercase",
                                                        fontWeight: "bolder",
                                                    }}
                                                >
                                                    Destination
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        fontSize: "11px",
                                                        backgroundColor: "#435560",
                                                        padding: "1px",
                                                        color: "whitesmoke",
                                                        textTransform: "uppercase",
                                                        fontWeight: "bolder",
                                                    }}
                                                >
                                                    Remarks
                                                </TableCell>

                                            </TableRow>
                                        </TableHead>

                                        <TableBody>
                                            {containerItems.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={7} align="center" style={{ fontSize: "12px", padding: "10px" }}>
                                                        No containers added yet.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                containerItems.map((item, index) => (
                                                    <TableRow key={index} hover>
                                                        {/* Sl.No (not editable) */}
                                                        <TableCell
                                                            style={{
                                                                fontSize: "12px",
                                                                padding: "6px",
                                                                textAlign: "center",
                                                                borderRight: "1px solid #D6DAC8",
                                                                width: "50px",
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </TableCell>

                                                        {/* Container Number */}
                                                        <TableCell style={{ fontSize: "12px", padding: "6px", borderRight: "1px solid #D6DAC8", width: "200px", fontWeight: 'bold' }}>
                                                            {item.ContainerNumber}
                                                        </TableCell>

                                                        {/* Source (dropdown when editing) */}
                                                        <TableCell style={{ fontSize: "12px", padding: "6px", borderRight: "1px solid #D6DAC8", width: "300px", fontWeight: 'bold' }}>
                                                            {item.SourceName}
                                                        </TableCell>

                                                        {/* Destination (dropdown when editing) */}
                                                        <TableCell style={{ fontSize: "12px", padding: "6px", borderRight: "1px solid #D6DAC8", width: "300px", fontWeight: 'bold' }}>
                                                            {item.DestinationName}
                                                        </TableCell>

                                                        {/* Remarks */}
                                                        <TableCell style={{ fontSize: "12px", padding: "6px", borderRight: "1px solid #D6DAC8", fontWeight: 'bold' }}>
                                                            {item.Remarks}
                                                        </TableCell>

                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>

                                    </Table>
                                </TableContainer>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            <div
                className="w-full flex flex-col"
                style={{
                    backgroundColor: "whitesmoke",
                    padding: "7px",
                    borderRadius: "10px",
                    marginTop: "10px",
                    boxShadow: "0 6px 10px rgba(0, 0, 0, 0.25), 0 12px 25px rgba(0, 0, 0, 0.2)"
                }}
            >
                {/* Header with expand/collapse button */}
                <div className="flex justify-between items-center">
                    <h6
                        className="uppercase tracking-wide font-serif mt-2"
                        style={{ color: "#0c3102ff", fontSize: '14px' }}
                    >
                        Expense List
                    </h6>

                    <button
                        onClick={() => setIsExpandedExpense(!isExpandedExpense)}
                        className="text-sm text-blue-600 flex items-center gap-1"
                        style={{ color: "#0c3102ff" }}
                    >
                        {isExpandedExpense ? (
                            <>
                                <ExpandLessIcon size={16} />
                            </>
                        ) : (
                            <>
                                <ExpandMoreIcon size={16} />
                            </>
                        )}
                    </button>
                </div>

                {/* Expandable Section */}
                {isExpandedExpense && (
                    <div className="w-full mt-1">

                        {/* Table Section */}
                        <div className="w-full pr-4" style={{ marginTop: "10px" }}>
                            <div
                                className="mb-2 flex"
                                style={{
                                    maxHeight: "450px",
                                    overflowY: "auto",
                                    marginTop: "-15px",
                                }}
                            >
                                <TableContainer component={Paper}>
                                    <Table stickyHeader>
                                        <TableHead stickyHeader>
                                            <TableRow>
                                                <TableCell
                                                    style={{
                                                        fontSize: "11px",
                                                        backgroundColor: "#435560",
                                                        padding: "1px",
                                                        color: "whitesmoke",
                                                        textTransform: "uppercase",
                                                        fontWeight: "bolder",
                                                    }}
                                                >
                                                    Sl.No
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        fontSize: "11px",
                                                        backgroundColor: "#435560",
                                                        padding: "1px",
                                                        color: "whitesmoke",
                                                        textTransform: "uppercase",
                                                        fontWeight: "bolder",
                                                    }}>
                                                    Expense Head
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        fontSize: "11px",
                                                        backgroundColor: "#435560",
                                                        padding: "1px",
                                                        color: "whitesmoke",
                                                        textTransform: "uppercase",
                                                        fontWeight: "bolder",
                                                    }}>
                                                    Remarks
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        fontSize: "11px",
                                                        backgroundColor: "#435560",
                                                        padding: "1px",
                                                        color: "whitesmoke",
                                                        textTransform: "uppercase",
                                                        fontWeight: "bolder",
                                                    }}
                                                >
                                                    Amount (RO)
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        fontSize: "11px",
                                                        backgroundColor: "#435560",
                                                        padding: "1px",
                                                        color: "whitesmoke",
                                                        textTransform: "uppercase",
                                                        fontWeight: "bolder",
                                                    }}
                                                >
                                                    VAT %
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        fontSize: "11px",
                                                        backgroundColor: "#435560",
                                                        padding: "1px",
                                                        color: "whitesmoke",
                                                        textTransform: "uppercase",
                                                        fontWeight: "bolder",
                                                    }}
                                                >
                                                    VAT Amount (RO)
                                                </TableCell>
                                                <TableCell
                                                    style={{
                                                        fontSize: "11px",
                                                        backgroundColor: "#435560",
                                                        padding: "1px",
                                                        color: "whitesmoke",
                                                        textTransform: "uppercase",
                                                        fontWeight: "bolder",
                                                    }}
                                                >
                                                    Total Amount (RO)
                                                </TableCell>

                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {expenseItems.length === 0 ? (
                                                <TableRow>
                                                    <TableCell colSpan={7} align="center" style={{ fontSize: "12px", padding: "10px" }}>
                                                        No Expense added yet.
                                                    </TableCell>
                                                </TableRow>
                                            ) : (
                                                expenseItems.map((item, index) => (
                                                    <TableRow key={index} hover>
                                                        {/* Sl.No (not editable) */}
                                                        <TableCell
                                                            style={{
                                                                fontSize: "12px",
                                                                padding: "6px",
                                                                textAlign: "center",
                                                                borderRight: "1px solid #D6DAC8",
                                                                width: "50px",
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {index + 1}
                                                        </TableCell>
                                                        {/* IncomeExpenseName (dropdown when editing) */}
                                                        <TableCell style={{ fontSize: "12px", padding: "6px", borderRight: "1px solid #D6DAC8", width: "300px", fontWeight: 'bold' }}>
                                                            {item.IncomeExpenseName}
                                                        </TableCell>
                                                        {/* Remarks */}
                                                        <TableCell style={{ fontSize: "12px", padding: "6px", borderRight: "1px solid #D6DAC8", fontWeight: 'bold' }}>
                                                            {item.Remarks}
                                                        </TableCell>
                                                        {/* Amount */}
                                                        <TableCell
                                                            style={{
                                                                fontSize: "12px",
                                                                padding: "6px",
                                                                textAlign: "right",
                                                                borderRight: "1px solid #D6DAC8",
                                                                width: "120px",
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {item.Amount ? parseFloat(item.Amount).toFixed(3) : "0.000"}
                                                        </TableCell>
                                                        {/* VAT Rate */}
                                                        <TableCell
                                                            style={{
                                                                fontSize: "12px",
                                                                padding: "6px",
                                                                textAlign: "right",
                                                                borderRight: "1px solid #D6DAC8",
                                                                width: "80px",
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {item.VatRate ? parseFloat(item.VatRate).toFixed(3) : "0.000"}
                                                        </TableCell>
                                                        {/* VAT Amount */}
                                                        <TableCell
                                                            style={{
                                                                fontSize: "12px",
                                                                padding: "6px",
                                                                textAlign: "right",
                                                                borderRight: "1px solid #D6DAC8",
                                                                width: "120px",
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {item.VatAmount ? parseFloat(item.VatAmount).toFixed(3) : "0.000"}
                                                        </TableCell>
                                                        {/* Total Amount */}
                                                        <TableCell
                                                            style={{
                                                                fontSize: "12px",
                                                                padding: "6px",
                                                                textAlign: "right",
                                                                borderRight: "1px solid #D6DAC8",
                                                                width: "120px",
                                                                fontWeight: 'bold'
                                                            }}
                                                        >
                                                            {item.TotalAmount ? parseFloat(item.TotalAmount).toFixed(3) : "0.000"}
                                                        </TableCell>

                                                    </TableRow>
                                                ))
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </div>
                        </div>
                    </div>
                )}
            </div>

        </Fragment >
    );
};

export default InvoiceDetails;
